import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateRangeCalendar } from "@/components/DateRangeCalendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { resolveRange, rangeText } from "@/lib/dateRange";

const PRESETS = [
  { value: "7d",         label: "近 7 天"  },
  { value: "14d",        label: "近 14 天" },
  { value: "30d",        label: "近 30 天" },
  { value: "90d",        label: "近 90 天" },
  { value: "this_month", label: "本月"     },
  { value: "last_month", label: "上月"     },
  { value: "all",        label: "全部時間" },
  { value: "custom",     label: "自訂"     },
];

// T2: Smart label when custom range matches a known preset shape
function prettyCustomLabel(start, end) {
  if (!start || !end) return "自訂";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(0, 0, 0, 0);
  const diffDays = Math.round((e - s) / 86400000) + 1;
  const isToday = e.getTime() === today.getTime();
  if (isToday && [7, 30, 90].includes(diffDays)) return `近 ${diffDays} 天`;
  const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
  if (s.getTime() === firstThis.getTime() && isToday) return `${today.getFullYear()} 本月`;
  const lastThisEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const lastThisStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  if (s.getTime() === lastThisStart.getTime() && e.getTime() === lastThisEnd.getTime()) return `${lastThisStart.getFullYear()} 上月`;
  if (s.getFullYear() < 2024) return "全部時間";
  return `${format(s, "yyyy-MM-dd")} ~ ${format(e, "yyyy-MM-dd")}`;
}

export default function TimeRangePicker({ value, onChange, syncUrl = true, testid = "time-range-picker" }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customOpen, setCustomOpen] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  // Initial read from URL
  useEffect(() => {
    if (!syncUrl) return;
    const r = searchParams.get("range");
    const s = searchParams.get("start");
    const e = searchParams.get("end");
    if (r && (r !== value || (r === "custom" && s && e))) {
      onChange?.({ range: r, start: s, end: e });
    }
    if (s) setStart(new Date(s));
    if (e) setEnd(new Date(e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v) => {
    if (v === "custom") {
      setCustomOpen(true);
      return;
    }
    // G1 — update URL first so a picker re-mounted by the page's loading swap reads the NEW range (not the old one)
    if (syncUrl) {
      const next = new URLSearchParams(searchParams);
      next.set("range", v);
      next.delete("start"); next.delete("end");
      setSearchParams(next, { replace: true });
    }
    onChange?.({ range: v });
  };

  const applyCustom = ({ since: sStr, until: eStr }) => {
    setStart(parseISO(sStr)); setEnd(parseISO(eStr));
    if (syncUrl) {
      const next = new URLSearchParams(searchParams);
      next.set("range", "custom");
      next.set("start", sStr);
      next.set("end", eStr);
      setSearchParams(next, { replace: true });
    }
    onChange?.({ range: "custom", start: sStr, end: eStr });
    setCustomOpen(false);
  };

  const activeLabel = value === "custom" && start && end
    ? prettyCustomLabel(start, end)
    : PRESETS.find((p) => p.value === value)?.label || "近 30 天";


  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
      {PRESETS.map((p) => {
        if (p.value === "custom") {
          return (
            <Popover key={p.value} open={customOpen} onOpenChange={setCustomOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={() => pick(p.value)}
                  className={`inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs border transition-colors ${
                    value === "custom"
                      ? "bg-[#F26A1F] border-[#F26A1F] text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`${testid}-${p.value}`}
                >
                  <CalendarIcon className="h-3 w-3" />
                  {value === "custom" ? activeLabel : p.label}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-slate-200" align="end">
                {customOpen && <DateRangeCalendar since={start} until={end} min="2024-01-01" testid={`${testid}-custom`}
                  onCancel={() => setCustomOpen(false)} onApply={applyCustom} />}
              </PopoverContent>
            </Popover>
          );
        }
        return (
          <button
            key={p.value}
            type="button"
            onClick={() => pick(p.value)}
            className={`h-8 px-3 rounded-md text-xs border transition-colors ${
              value === p.value
                ? "bg-[#F26A1F] border-[#F26A1F] text-white"
                : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
            data-testid={`${testid}-${p.value}`}
          >
            {p.label}
          </button>
        );
      })}
      <span className="ml-1 font-mono text-[11px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 bg-white" data-testid={`${testid}-resolved`}>
        {rangeText(resolveRange(value, start && format(start, "yyyy-MM-dd"), end && format(end, "yyyy-MM-dd")))}
      </span>
    </div>
  );
}
